import React, { useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { CertificateOutletType } from '..'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ParamType } from '../CreateParam'
import ViewParamModal from '../../../../components/Certificates/ViewParamModal'
import { Copy, Eye, Pencil } from 'lucide-react'
import { matchAllFilters } from '../../../../utils/filterMatcher'
import { StandardColumn, StandardDataTable, useColumnsState } from '../../../../components/StandardPage'

const ParamTableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

import { OverflowCell } from '../../../../components/OverflowCell'

const paramsScreenKey = 'certificate_params'

const formatParamStructureText = (items?: ParamType['mechanicalTests']): string => {
  if (!items || items.length === 0) return 'Nenhum'
  return items
    .map((item) => {
      let range = ''
      if (item.min != null && item.max != null) range = ` (${item.min} - ${item.max}${item.unit ? ` ${item.unit}` : ''})`
      else if (item.min != null) range = ` (mín ${item.min}${item.unit ? ` ${item.unit}` : ''})`
      else if (item.max != null) range = ` (máx ${item.max}${item.unit ? ` ${item.unit}` : ''})`
      else if (item.unit) range = ` (${item.unit})`
      return `${item.name}${range}`
    })
    .join(', ')
}

const renderParamStructureCell = (items?: ParamType['mechanicalTests']) => {
  if (!items || items.length === 0) return 'Nenhum'
  const inlineText = items
    .map((item) => {
      let range = ''
      if (item.min != null && item.max != null) range = ` (${item.min} - ${item.max}${item.unit ? ` ${item.unit}` : ''})`
      else if (item.min != null) range = ` (mín ${item.min}${item.unit ? ` ${item.unit}` : ''})`
      else if (item.max != null) range = ` (máx ${item.max}${item.unit ? ` ${item.unit}` : ''})`
      else if (item.unit) range = ` (${item.unit})`
      return `${item.name}${range}`
    })
    .join(', ')

  const tooltipText = items
    .map((item) => {
      let range = ''
      if (item.min != null && item.max != null) range = `: ${item.min} a ${item.max}${item.unit ? ` ${item.unit}` : ''}`
      else if (item.min != null) range = `: mín ${item.min}${item.unit ? ` ${item.unit}` : ''}`
      else if (item.max != null) range = `: máx ${item.max}${item.unit ? ` ${item.unit}` : ''}`
      else if (item.unit) range = `: ${item.unit}`
      return `${item.name}${range}`
    })
    .join('\n')

  return (
    <OverflowCell text={tooltipText} maxWidth={200} forceTooltip={items.length > 1}>
      {inlineText}
    </OverflowCell>
  )
}

const buildParamColumns = (): StandardColumn<ParamType>[] => [
  {
    id: 'name',
    label: 'Nome do parâmetro',
    accessor: (param) => param.name || '-',
    cell: (param) => <OverflowCell text={param.name || '-'} maxWidth={200} />,
  },
  {
    id: 'certificateType',
    label: 'Tipo de certificado',
    accessor: (param) => param.certificateType || param.CertificateType?.name || param.type?.name || '-',
    cell: (param) => <OverflowCell text={param.certificateType || param.CertificateType?.name || param.type?.name || '-'} maxWidth={150} />,
  },
  {
    id: 'chemicalComposition',
    label: 'Composição química',
    sortable: false,
    disableBulkEdit: true,
    accessor: (param) => formatParamStructureText(param.chemicalComposition),
    cell: (param) => renderParamStructureCell(param.chemicalComposition),
    exportValue: (param) => formatParamStructureText(param.chemicalComposition),
  },
  {
    id: 'mechanicalTests',
    label: 'Ensaios mecânicos',
    sortable: false,
    disableBulkEdit: true,
    accessor: (param) => formatParamStructureText(param.mechanicalTests),
    cell: (param) => renderParamStructureCell(param.mechanicalTests),
    exportValue: (param) => formatParamStructureText(param.mechanicalTests),
  },
  { id: 'class', label: 'Condição', accessor: (param) => param.class || '-' },
  { id: 'family', label: 'Família', accessor: (param) => param.family || '-' },
  { id: 'astm', label: 'ASTM', accessor: (param) => param.astm || '-' },
  { id: 'uns', label: 'UNS', accessor: (param) => param.uns || '-' },
  { id: 'din', label: 'DIN', accessor: (param) => param.din || '-' },
  { id: 'aisi', label: 'AISI', accessor: (param) => param.aisi || '-' },
  { id: 'others', label: 'Outras', accessor: (param) => param.others || '-' },
]

const ParamTable = () => {
  const [parameters, setParameters] = useState<ParamType[]>([])
  const [filteredParameters, setFilteredParameters] = useState<ParamType[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [viewParamId, setViewParamId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { appliedFilters, search, refresh, setLoadingHeader } = useOutletContext<CertificateOutletType>()
  const navigate = useNavigate()

  const columns = useMemo(() => buildParamColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(paramsScreenKey, columns)

  const fetchParams = () => {
    setIsLoading(true)
    setLoadingHeader?.(true)
    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/param`)
      .then(response => {
        if(response.data) {
          setParameters(response.data)
          setFilteredParameters(response.data)
        }
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setIsLoading(false)
        setLoadingHeader?.(false)
      })
  }

  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (refresh) fetchParams()
  }, [refresh])

  useEffect(() => {
    fetchParams()
  }, [])

  useEffect(() => {
    let filtered = [...parameters]

    if (search && search.trim()) {
      filtered = filtered.filter(param =>
        param.name?.toLowerCase().includes(search.toLowerCase()) ||
        param.certificateType?.toLowerCase().includes(search.toLowerCase()) ||
        param.CertificateType?.name?.toLowerCase().includes(search.toLowerCase()) ||
        param.type?.name?.toLowerCase().includes(search.toLowerCase()) ||
        param.class?.toLowerCase().includes(search.toLowerCase()) ||
        param.family?.toLowerCase().includes(search.toLowerCase()) ||
        param.astm?.toLowerCase().includes(search.toLowerCase()) ||
        param.uns?.toLowerCase().includes(search.toLowerCase()) ||
        param.din?.toLowerCase().includes(search.toLowerCase()) ||
        param.aisi?.toLowerCase().includes(search.toLowerCase()) ||
        param.others?.toLowerCase().includes(search.toLowerCase()) ||
        formatParamStructureText(param.chemicalComposition).toLowerCase().includes(search.toLowerCase()) ||
        formatParamStructureText(param.mechanicalTests).toLowerCase().includes(search.toLowerCase())
      )
    }

    if (appliedFilters.length > 0) {
      filtered = filtered.filter(param => matchAllFilters(param as unknown as Record<string, any>, appliedFilters))
    }

    setFilteredParameters(filtered)
  }, [parameters, appliedFilters, search])

  return (
    <ParamTableRoot>
      <StandardDataTable
        columns={columns}
        data={filteredParameters.slice(0, visibleCount)}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={filteredParameters.length}
        onLoadMore={() => setVisibleCount(prev => prev + 50)}
        hasMore={visibleCount < filteredParameters.length}
        isLoading={isLoading}
        countLabel='parâmetros'
        tableName='Param'
        exportFileName='parametros'
        emptyMessage='Nenhum parâmetro encontrado.'
        rowActions={(param) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => param.id && setViewParamId(param.id) },
          { label: 'Duplicar', icon: <Copy size={16} />, onClick: () => param.id && navigate(`/panel/certificates/params/create?duplicateFrom=${param.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`/panel/certificates/params/edit?id=${param.id}`) },
        ]}
      />
      {viewParamId !== null && (
        <ViewParamModal
          paramId={viewParamId}
          onClose={() => setViewParamId(null)}
        />
      )}
    </ParamTableRoot>
  )
}

export default ParamTable
